if (!customElements.get('media-gallery')) {
    customElements.define('media-gallery', class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          firstImage: this.querySelector('[id^="GalleryViewer"] .media')
        }
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;
  
        // Update thumbnails immediately on slide change (remove debounce for instant UI feedback)
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));

        // Also update thumbnails immediately when nav buttons are clicked, without waiting for scroll/update
        const prevBtn = this.elements.viewer.querySelector('button[name="previous"]');
        const nextBtn = this.elements.viewer.querySelector('button[name="next"]');
        if (prevBtn) prevBtn.addEventListener('click', this.onViewerNavClick.bind(this));
        if (nextBtn) nextBtn.addEventListener('click', this.onViewerNavClick.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();

        if (this.dataset.setHeight) {
          this.lastValidHeight = null;
          window.setTimeout(this.setHeight.bind(this));
          this.resizeObserver = new ResizeObserver(this.setHeight.bind(this));
          this.resizeObserver.observe(this.elements.viewer);
          this.mediaResizeObserver = new ResizeObserver(this.setHeight.bind(this));
          
          // Listen to scroll events for mobile swiping
          // Wait for slider to be initialized, then attach scroll listeners
          this.setupScrollListeners();
        }
      }
  
      setupScrollListeners() {
        // Try to get slider from viewer component, or find it directly
        const getSlider = () => {
          if (this.elements.viewer?.slider) {
            return this.elements.viewer.slider;
          }
          // Fallback: find the slider element directly
          return this.elements.viewer?.querySelector('[id^="Slider-Gallery"]') || 
                 this.elements.viewer?.querySelector('.slider');
        };
        
        const slider = getSlider();
        if (slider) {
          this.scrollTimeout = null;
          this.scrollSlider = slider;
          slider.addEventListener('scroll', this.onScroll.bind(this));
          // Use scrollend event if available (modern browsers)
          if ('onscrollend' in window) {
            slider.addEventListener('scrollend', this.onScrollEnd.bind(this));
          }
        } else {
          // Retry after a short delay if slider isn't ready yet
          window.setTimeout(() => this.setupScrollListeners(), 100);
        }
      }

      onSlideChanged(event) {
        const currentElement = event && event.detail && event.detail.currentElement;
        if (!currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ currentElement.dataset.mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
        if (this.dataset.setHeight) {
          // Use requestAnimationFrame to ensure DOM has updated
          // Also use visible slide detection in case is-active class hasn't updated yet
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.setTimeout(() => {
                this.setHeight(true);
              }, 50);
            });
          });
        }
      }

      onScroll() {
        if (!this.dataset.setHeight) return;
        
        // Debounce scroll events to avoid excessive updates
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
        }
        
        this.scrollTimeout = window.setTimeout(() => {
          // Update height during scroll for mobile swiping
          // Use a longer delay to let scroll-snap settle
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.setHeight(true);
            });
          });
        }, 100);
      }

      onScrollEnd() {
        if (!this.dataset.setHeight) return;
        
        // Clear any pending scroll timeout
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = null;
        }
        
        // Update height when scroll ends (for mobile swipe completion)
        // Give it time for scroll-snap to complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.setTimeout(() => {
              this.setHeight(true);
            }, 50);
          });
        });
      }

      onViewerNavClick(event) {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.sliderItemsToShow || viewer.sliderItemsToShow.length === 0) return;

        const total = viewer.sliderItemsToShow.length;
        const step = parseInt(event.currentTarget.dataset.step || '1', 10);
        const goingNext = event.currentTarget.name === 'next';

        let current = viewer.currentPage || 1;
        let target = goingNext ? current + step : current - step;

        // Wrap if slideshow loops
        if (target > total) target = 1;
        if (target < 1) target = total;

        const targetElement = viewer.sliderItemsToShow[target - 1];
        if (!targetElement) return;

        const mediaId = targetElement.dataset.mediaId || (targetElement.querySelector('[data-media-id]') && targetElement.querySelector('[data-media-id]').dataset.mediaId);
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setHeight(useVisibleSlide = false) {
        if (!this.elements.viewer) return;
        let activeSlide = null;
        
        // When scrolling, prioritize finding the visible slide over is-active class
        // because is-active might not be updated yet during scroll
        if (useVisibleSlide) {
          activeSlide = this.findVisibleSlide();
          // Fallback to is-active if visible slide not found
          if (!activeSlide) {
            activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
          }
        } else {
          // Normal case: use is-active class
          activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
        }
        
        let height = null;
        
        if (activeSlide) {
          const mediaElement = activeSlide.querySelector('.media');
          if (mediaElement) {
            // Update ResizeObserver to watch the active media element
            if (this.mediaResizeObserver) {
              this.mediaResizeObserver.disconnect();
              this.mediaResizeObserver.observe(mediaElement);
            }
            
            // Check for images that need to load
            const images = mediaElement.querySelectorAll('img');
            const unloadedImages = Array.from(images).filter(img => !img.complete);
            
            if (unloadedImages.length > 0) {
              // Wait for images to load, then recalculate
              let loadedCount = 0;
              const totalUnloaded = unloadedImages.length;
              
              const onImageLoad = () => {
                loadedCount++;
                if (loadedCount === totalUnloaded) {
                  // All images loaded, recalculate height
                  window.setTimeout(() => this.calculateAndSetHeight(activeSlide, mediaElement), 0);
                }
              };
              
              unloadedImages.forEach(img => {
                img.addEventListener('load', onImageLoad, { once: true });
                img.addEventListener('error', onImageLoad, { once: true });
              });
            }
            
            // Calculate height immediately (will update again when images load if needed)
            height = this.calculateHeight(activeSlide, mediaElement);
          } else if (activeSlide.offsetHeight > 0) {
            height = activeSlide.offsetHeight;
          }
        } else if (this.elements.viewer.offsetHeight > 0) {
          height = this.elements.viewer.offsetHeight;
        }
        
        // Only update if we have a valid height, otherwise keep the last valid height
        if (height && height > 0) {
          this.lastValidHeight = height;
          this.style.setProperty('--height', `${height + 1}px`);
        } else if (this.lastValidHeight) {
          this.style.setProperty('--height', `${this.lastValidHeight + 1}px`);
        }
      }

      findVisibleSlide() {
        if (!this.elements.viewer) return null;
        
        const slider = this.scrollSlider || this.elements.viewer.slider || 
                      this.elements.viewer.querySelector('[id^="Slider-Gallery"]') ||
                      this.elements.viewer.querySelector('.slider');
        if (!slider) return null;
        
        const scrollLeft = slider.scrollLeft;
        const sliderWidth = slider.clientWidth;
        const viewportRight = scrollLeft + sliderWidth;
        
        // Find the slide that's most visible in the viewport
        // Prefer slides that are more centered, but also consider how much of the slide is visible
        const slides = this.elements.viewer.querySelectorAll('[data-media-id]');
        let bestSlide = null;
        let bestScore = -1;
        
        slides.forEach(slide => {
          const slideLeft = slide.offsetLeft;
          const slideRight = slideLeft + slide.offsetWidth;
          const slideWidth = slide.offsetWidth;
          
          // Check if slide overlaps with viewport
          if (slideRight > scrollLeft && slideLeft < viewportRight) {
            // Calculate how much of the slide is visible
            const visibleLeft = Math.max(slideLeft, scrollLeft);
            const visibleRight = Math.min(slideRight, viewportRight);
            const visibleWidth = Math.max(0, visibleRight - visibleLeft);
            const visibleRatio = visibleWidth / slideWidth;
            
            // Calculate how centered the slide is
            const slideCenter = slideLeft + (slideWidth / 2);
            const viewportCenter = scrollLeft + (sliderWidth / 2);
            const centerDistance = Math.abs(slideCenter - viewportCenter);
            const centerScore = 1 / (1 + centerDistance / sliderWidth); // Closer to center = higher score
            
            // Combined score: prefer slides that are both visible and centered
            const score = visibleRatio * 0.6 + centerScore * 0.4;
            
            if (score > bestScore) {
              bestScore = score;
              bestSlide = slide;
            }
          }
        });
        
        return bestSlide;
      }

      calculateHeight(activeSlide, mediaElement) {
        if (mediaElement && mediaElement.offsetHeight > 0) {
          return mediaElement.offsetHeight;
        }
        if (activeSlide && activeSlide.offsetHeight > 0) {
          return activeSlide.offsetHeight;
        }
        return null;
      }

      calculateAndSetHeight(activeSlide, mediaElement) {
        const height = this.calculateHeight(activeSlide, mediaElement);
        if (height && height > 0) {
          this.lastValidHeight = height;
          this.style.setProperty('--height', `${height + 1}px`);
        }
      }
  
      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
  
        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }
  
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({behavior: 'smooth'});
          }
        });
        this.playActiveMedia(activeMedia);
  
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        
        if (this.dataset.setHeight) {
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            window.setTimeout(this.setHeight.bind(this), 0);
          });
        }
      }
  
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
  
        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const isVertical = this.elements.thumbnails.offsetHeight > this.elements.thumbnails.offsetWidth;

        // Determine orientation by comparing the width and height of the slider container
      
        // Scroll to the thumbnail depending on orientation
        const previousThumbnail = thumbnail.previousElementSibling;
        if(previousThumbnail) {

        const scrollOptions = isVertical
          ? { top: (previousThumbnail || thumbnail).offsetTop - 2, behavior: 'smooth' }
          : { left: thumbnail.offsetLeft, behavior: 'smooth' };
        

      
          this.elements.thumbnails.slider.scrollTo(scrollOptions);
        }
      }
  
      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }
  
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }
  
      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
  
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
      }
    });
  }